import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { useToast } from '../../ui/use-toast';
import { 
  Activity,
  Users,
  Package,
  Clock,
  Calendar,
  Download,
  Filter,
  Building,
  CheckCircle,
  TrendingUp,
  Hammer,
  FileSpreadsheet
} from 'lucide-react';
import { User, Team, Project } from '../../../types';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays, subMonths } from 'date-fns';
import { ro } from 'date-fns/locale';
import { apiService } from '../../../services/api';
import * as XLSX from 'xlsx';

interface ActivitiesReportTabProps {
  allUsers: User[];
  teams: Team[];
  projects: Project[];
  selectedDate: string;
  onDateChange: (date: string) => void;
  isLoading: boolean;
}

interface ActivityReport {
  date: string;
  teamId: number;
  teamName: string;
  supervisorName: string;
  userId: number;
  userName: string;
  activityName: string;
  quantity: number;
  unit: string;
  projectId?: number;
  projectName?: string;
}

interface MaterialReport {
  date: string;
  teamId: number;
  teamName: string;
  materialName: string;
  quantity: number;
  unit: string;
  type: 'consumed' | 'given';
  projectId?: number;
  projectName?: string;
}

interface OvertimeReport {
  date: string;
  teamId: number;
  teamName: string;
  userId: number;
  userName: string;
  overtimeHours: number;
  reason: string;
  approvedBy: string;
  approvedAt: string;
}

interface TeamSummary {
  teamId: number;
  teamName: string;
  supervisorName: string;
  totalActivities: number;
  totalMaterialsConsumed: number;
  totalMaterialsGiven: number;
  totalOvertimeHours: number;
  workersCount: number;
  activities: ActivityReport[];
  materials: MaterialReport[];
  overtime: OvertimeReport[];
}

export const ActivitiesReportTab: React.FC<ActivitiesReportTabProps> = ({
  allUsers,
  teams,
  projects,
  selectedDate,
  onDateChange,
  isLoading
}) => {
  const [period, setPeriod] = useState<'day' | 'week' | 'month'>('day');
  const [selectedTeam, setSelectedTeam] = useState<string>('all');
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [selectedSupervisor, setSelectedSupervisor] = useState<string>('all');
  
  const [activities, setActivities] = useState<ActivityReport[]>([]);
  const [materials, setMaterials] = useState<MaterialReport[]>([]);
  const [overtimeRecords, setOvertimeRecords] = useState<OvertimeReport[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  
  const { toast } = useToast();

  // Get supervisors
  const supervisors = useMemo(() => 
    allUsers.filter(u => u.role === 'supervisor' || u.role === 'teamlead'),
    [allUsers]
  );

  // Calculate date range based on period
  const dateRange = useMemo(() => {
    const date = new Date(selectedDate);
    let start: Date, end: Date;
    
    switch (period) {
      case 'week':
        start = startOfWeek(date, { weekStartsOn: 1 });
        end = endOfWeek(date, { weekStartsOn: 1 });
        break;
      case 'month':
        start = startOfMonth(date);
        end = endOfMonth(date);
        break;
      default:
        start = date;
        end = date;
    }
    
    return { start, end };
  }, [selectedDate, period]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingData(true);
      try {
        // Fetch activities for date range using the new endpoint
        let activitiesResponse: any[] = [];
        try {
          const response = await apiService.getActivitiesByDateRange({
            startDate: format(dateRange.start, 'yyyy-MM-dd'),
            endDate: format(dateRange.end, 'yyyy-MM-dd'),
            teamId: selectedTeam !== 'all' ? parseInt(selectedTeam) : undefined,
            projectId: selectedProject !== 'all' ? parseInt(selectedProject) : undefined,
            supervisorId: selectedSupervisor !== 'all' ? parseInt(selectedSupervisor) : undefined
          });
          
          if (response && Array.isArray(response)) {
            activitiesResponse = response;
          }
        } catch (err) {
          console.error('Error fetching activities:', err);
        }
        
        // Transform activities data
        const activityReports: ActivityReport[] = [];
        if (activitiesResponse && Array.isArray(activitiesResponse)) {
          activitiesResponse.forEach((activity: any) => {
            const user = allUsers.find(u => u.id === activity.userId);
            const team = teams.find(t => t.id === user?.teamId);
            const supervisor = allUsers.find(u => u.id === team?.supervisorId);
            
            activityReports.push({
              date: activity.date,
              teamId: team?.id || 0,
              teamName: team?.name || 'Unknown',
              supervisorName: supervisor?.name || 'Unknown',
              userId: activity.userId,
              userName: user?.name || 'Unknown',
              activityName: activity.name,
              quantity: activity.quantity,
              unit: activity.unit,
              projectId: activity.projectId,
              projectName: projects.find(p => p.id === activity.projectId)?.name
            });
          });
        }
        setActivities(activityReports);

        // Fetch materials for date range using the new endpoint
        let materialsResponse: any[] = [];
        try {
          const response = await apiService.getMaterialConsumptionsByDateRange({
            startDate: format(dateRange.start, 'yyyy-MM-dd'),
            endDate: format(dateRange.end, 'yyyy-MM-dd'),
            teamId: selectedTeam !== 'all' ? parseInt(selectedTeam) : undefined,
            projectId: selectedProject !== 'all' ? parseInt(selectedProject) : undefined,
            supervisorId: selectedSupervisor !== 'all' ? parseInt(selectedSupervisor) : undefined
          });
          
          if (response && Array.isArray(response)) {
            materialsResponse = response;
          }
        } catch (err) {
          console.error('Error fetching materials:', err);
        }
        
        // Transform materials data
        const materialReports: MaterialReport[] = [];
        if (materialsResponse && Array.isArray(materialsResponse)) {
          materialsResponse.forEach((material: any) => {
            const team = teams.find(t => t.id === material.teamId);
            
            materialReports.push({
              date: material.date,
              teamId: material.teamId,
              teamName: team?.name || 'Unknown',
              materialName: material.materialName,
              quantity: material.quantity,
              unit: material.unit,
              type: material.type || 'consumed',
              projectId: material.projectId,
              projectName: projects.find(p => p.id === material.projectId)?.name
            });
          });
        }
        setMaterials(materialReports);

        // Fetch approved overtime
        const overtimeResponse = await apiService.getApprovedOvertime({
          startDate: format(dateRange.start, 'yyyy-MM-dd'),
          endDate: format(dateRange.end, 'yyyy-MM-dd')
        });
        
        // Transform overtime data
        const overtimeReports: OvertimeReport[] = [];
        if (overtimeResponse && Array.isArray(overtimeResponse)) {
          overtimeResponse.forEach((overtime: any) => {
            const user = allUsers.find(u => u.id === overtime.userId);
            const team = teams.find(t => t.id === user?.teamId);
            
            overtimeReports.push({
              date: overtime.date,
              teamId: team?.id || 0,
              teamName: team?.name || 'Unknown',
              userId: overtime.userId,
              userName: user?.name || 'Unknown',
              overtimeHours: overtime.overtimeHours,
              reason: overtime.reason || 'N/A',
              approvedBy: overtime.approvedByName || 'Unknown',
              approvedAt: overtime.approvedAt
            });
          });
        }
        setOvertimeRecords(overtimeReports);
        
      } catch (error) {
        console.error('Error fetching report data:', error);
        toast({
          title: 'Eroare',
          description: 'Nu s-au putut încărca datele raportului',
          variant: 'destructive'
        });
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, [dateRange, allUsers, teams, projects, toast]);

  // Filter data based on selections
  const filteredData = useMemo(() => {
    let filteredActivities = [...activities];
    let filteredMaterials = [...materials];
    let filteredOvertime = [...overtimeRecords];

    // Team filter
    if (selectedTeam !== 'all') {
      const teamId = parseInt(selectedTeam);
      filteredActivities = filteredActivities.filter(a => a.teamId === teamId);
      filteredMaterials = filteredMaterials.filter(m => m.teamId === teamId);
      filteredOvertime = filteredOvertime.filter(o => o.teamId === teamId);
    }

    // Project filter
    if (selectedProject !== 'all') {
      const projectId = parseInt(selectedProject);
      filteredActivities = filteredActivities.filter(a => a.projectId === projectId);
      filteredMaterials = filteredMaterials.filter(m => m.projectId === projectId);
    }

    // Supervisor filter
    if (selectedSupervisor !== 'all') {
      const supervisorTeams = teams.filter(t => t.supervisorId === parseInt(selectedSupervisor));
      const teamIds = supervisorTeams.map(t => t.id);
      filteredActivities = filteredActivities.filter(a => teamIds.includes(a.teamId));
      filteredMaterials = filteredMaterials.filter(m => teamIds.includes(m.teamId));
      filteredOvertime = filteredOvertime.filter(o => teamIds.includes(o.teamId));
    }

    return {
      activities: filteredActivities,
      materials: filteredMaterials,
      overtime: filteredOvertime
    };
  }, [activities, materials, overtimeRecords, selectedTeam, selectedProject, selectedSupervisor, teams]);

  // Group data by team
  const teamSummaries = useMemo((): TeamSummary[] => {
    const summaryMap = new Map<number, TeamSummary>();

    // Process activities
    filteredData.activities.forEach(activity => {
      if (!summaryMap.has(activity.teamId)) {
        summaryMap.set(activity.teamId, {
          teamId: activity.teamId,
          teamName: activity.teamName,
          supervisorName: activity.supervisorName,
          totalActivities: 0,
          totalMaterialsConsumed: 0,
          totalMaterialsGiven: 0,
          totalOvertimeHours: 0,
          workersCount: 0,
          activities: [],
          materials: [],
          overtime: []
        });
      }
      const summary = summaryMap.get(activity.teamId)!;
      summary.activities.push(activity);
      summary.totalActivities += activity.quantity;
    });

    // Process materials
    filteredData.materials.forEach(material => {
      if (!summaryMap.has(material.teamId)) {
        const team = teams.find(t => t.id === material.teamId);
        const supervisor = allUsers.find(u => u.id === team?.supervisorId);
        summaryMap.set(material.teamId, {
          teamId: material.teamId,
          teamName: material.teamName,
          supervisorName: supervisor?.name || 'Unknown',
          totalActivities: 0,
          totalMaterialsConsumed: 0,
          totalMaterialsGiven: 0,
          totalOvertimeHours: 0,
          workersCount: 0,
          activities: [],
          materials: [],
          overtime: []
        });
      }
      const summary = summaryMap.get(material.teamId)!;
      summary.materials.push(material);
      if (material.type === 'consumed') {
        summary.totalMaterialsConsumed += material.quantity;
      } else {
        summary.totalMaterialsGiven += material.quantity;
      }
    });

    // Process overtime
    filteredData.overtime.forEach(overtime => {
      if (!summaryMap.has(overtime.teamId)) {
        const team = teams.find(t => t.id === overtime.teamId);
        const supervisor = allUsers.find(u => u.id === team?.supervisorId);
        summaryMap.set(overtime.teamId, {
          teamId: overtime.teamId,
          teamName: overtime.teamName,
          supervisorName: supervisor?.name || 'Unknown',
          totalActivities: 0,
          totalMaterialsConsumed: 0,
          totalMaterialsGiven: 0,
          totalOvertimeHours: 0,
          workersCount: 0,
          activities: [],
          materials: [],
          overtime: []
        });
      }
      const summary = summaryMap.get(overtime.teamId)!;
      summary.overtime.push(overtime);
      summary.totalOvertimeHours += overtime.overtimeHours;
    });

    // Calculate unique workers count
    summaryMap.forEach(summary => {
      const uniqueWorkers = new Set([
        ...summary.activities.map(a => a.userId),
        ...summary.overtime.map(o => o.userId)
      ]);
      summary.workersCount = uniqueWorkers.size;
    });

    return Array.from(summaryMap.values());
  }, [filteredData, teams, allUsers]);

  // Export to Excel
  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Activities sheet
    const activitiesData = filteredData.activities.map(a => ({
      'Data': format(new Date(a.date), 'dd.MM.yyyy'),
      'Echipă': a.teamName,
      'Supervisor': a.supervisorName,
      'Muncitor': a.userName,
      'Activitate': a.activityName,
      'Cantitate': a.quantity,
      'Unitate': a.unit,
      'Proiect': a.projectName || 'N/A'
    }));
    const activitiesWs = XLSX.utils.json_to_sheet(activitiesData);
    XLSX.utils.book_append_sheet(wb, activitiesWs, 'Activități');

    // Materials sheet
    const materialsData = filteredData.materials.map(m => ({
      'Data': format(new Date(m.date), 'dd.MM.yyyy'),
      'Echipă': m.teamName,
      'Material': m.materialName,
      'Cantitate': m.quantity,
      'Unitate': m.unit,
      'Tip': m.type === 'consumed' ? 'Consumat' : 'Primit',
      'Proiect': m.projectName || 'N/A'
    }));
    const materialsWs = XLSX.utils.json_to_sheet(materialsData);
    XLSX.utils.book_append_sheet(wb, materialsWs, 'Materiale');

    // Overtime sheet
    const overtimeData = filteredData.overtime.map(o => ({
      'Data': format(new Date(o.date), 'dd.MM.yyyy'),
      'Echipă': o.teamName,
      'Muncitor': o.userName,
      'Ore Suplimentare': o.overtimeHours,
      'Motiv': o.reason,
      'Aprobat De': o.approvedBy,
      'Data Aprobare': format(new Date(o.approvedAt), 'dd.MM.yyyy HH:mm')
    }));
    const overtimeWs = XLSX.utils.json_to_sheet(overtimeData);
    XLSX.utils.book_append_sheet(wb, overtimeWs, 'Ore Suplimentare');

    // Summary sheet
    const summaryData = teamSummaries.map(s => ({
      'Echipă': s.teamName,
      'Supervisor': s.supervisorName,
      'Muncitori': s.workersCount,
      'Total Activități': s.totalActivities,
      'Materiale Consumate': s.totalMaterialsConsumed,
      'Materiale Primite': s.totalMaterialsGiven,
      'Ore Suplimentare': s.totalOvertimeHours
    }));
    const summaryWs = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summaryWs, 'Sumar');

    // Save file
    const fileName = `Raport_Activitati_${format(dateRange.start, 'yyyy-MM-dd')}_${format(dateRange.end, 'yyyy-MM-dd')}.xlsx`;
    XLSX.writeFile(wb, fileName);
    
    toast({
      title: 'Export reușit',
      description: `Raportul a fost salvat ca ${fileName}`
    });
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtre Raport Activități
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <Label>Perioadă</Label>
              <Select value={period} onValueChange={(v: any) => setPeriod(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Zi</SelectItem>
                  <SelectItem value="week">Săptămână</SelectItem>
                  <SelectItem value="month">Lună</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Data</Label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => onDateChange(e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>

            <div>
              <Label>Echipă</Label>
              <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate echipele</SelectItem>
                  {teams.map(team => (
                    <SelectItem key={team.id} value={team.id.toString()}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Proiect</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate proiectele</SelectItem>
                  {projects.map(project => (
                    <SelectItem key={project.id} value={project.id.toString()}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Supervisor</Label>
              <Select value={selectedSupervisor} onValueChange={setSelectedSupervisor}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toți supervizorii</SelectItem>
                  {supervisors.map(supervisor => (
                    <SelectItem key={supervisor.id} value={supervisor.id.toString()}>
                      {supervisor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-between items-center mt-4">
            <div className="text-sm text-gray-600">
              Perioada: {format(dateRange.start, 'dd MMM yyyy', { locale: ro })} - {format(dateRange.end, 'dd MMM yyyy', { locale: ro })}
            </div>
            <Button onClick={exportToExcel} disabled={isLoadingData}>
              <Download className="h-4 w-4 mr-2" />
              Export Excel
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Activități</p>
                <p className="text-2xl font-bold">
                  {filteredData.activities.reduce((sum, a) => sum + a.quantity, 0)}
                </p>
              </div>
              <Hammer className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Materiale Consumate</p>
                <p className="text-2xl font-bold">
                  {filteredData.materials.filter(m => m.type === 'consumed').length}
                </p>
              </div>
              <Package className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Ore Suplimentare</p>
                <p className="text-2xl font-bold text-green-600">
                  {filteredData.overtime.reduce((sum, o) => sum + o.overtimeHours, 0).toFixed(1)}h
                </p>
              </div>
              <Clock className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Echipe Active</p>
                <p className="text-2xl font-bold">
                  {teamSummaries.length}
                </p>
              </div>
              <Users className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Team Summaries */}
      <div className="space-y-4">
        {teamSummaries.map(summary => (
          <Card key={summary.teamId}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  {summary.teamName}
                </span>
                <Badge variant="outline">
                  Supervisor: {summary.supervisorName}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="activities">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="activities">
                    Activități ({summary.activities.length})
                  </TabsTrigger>
                  <TabsTrigger value="materials">
                    Materiale ({summary.materials.length})
                  </TabsTrigger>
                  <TabsTrigger value="overtime">
                    Ore Suplimentare ({summary.overtime.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="activities" className="space-y-2">
                  {summary.activities.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Nu există activități înregistrate</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2">Muncitor</th>
                            <th className="text-left p-2">Activitate</th>
                            <th className="text-right p-2">Cantitate</th>
                            <th className="text-left p-2">Unitate</th>
                            <th className="text-left p-2">Proiect</th>
                          </tr>
                        </thead>
                        <tbody>
                          {summary.activities.map((activity, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="p-2">{activity.userName}</td>
                              <td className="p-2">{activity.activityName}</td>
                              <td className="text-right p-2">{activity.quantity}</td>
                              <td className="p-2">{activity.unit}</td>
                              <td className="p-2">{activity.projectName || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="materials" className="space-y-2">
                  {summary.materials.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Nu există materiale înregistrate</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2">Material</th>
                            <th className="text-right p-2">Cantitate</th>
                            <th className="text-left p-2">Unitate</th>
                            <th className="text-left p-2">Tip</th>
                            <th className="text-left p-2">Proiect</th>
                          </tr>
                        </thead>
                        <tbody>
                          {summary.materials.map((material, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="p-2">{material.materialName}</td>
                              <td className="text-right p-2">{material.quantity}</td>
                              <td className="p-2">{material.unit}</td>
                              <td className="p-2">
                                <Badge variant={material.type === 'consumed' ? 'destructive' : 'default'}>
                                  {material.type === 'consumed' ? 'Consumat' : 'Primit'}
                                </Badge>
                              </td>
                              <td className="p-2">{material.projectName || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="overtime" className="space-y-2">
                  {summary.overtime.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Nu există ore suplimentare aprobate</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2">Muncitor</th>
                            <th className="text-right p-2">Ore</th>
                            <th className="text-left p-2">Motiv</th>
                            <th className="text-left p-2">Aprobat de</th>
                          </tr>
                        </thead>
                        <tbody>
                          {summary.overtime.map((overtime, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="p-2">{overtime.userName}</td>
                              <td className="text-right p-2">
                                <Badge variant="outline">{overtime.overtimeHours}h</Badge>
                              </td>
                              <td className="p-2">{overtime.reason}</td>
                              <td className="p-2">{overtime.approvedBy}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
                <div>
                  <p className="text-sm text-gray-600">Muncitori</p>
                  <p className="font-bold">{summary.workersCount}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Activități</p>
                  <p className="font-bold">{summary.totalActivities}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Materiale Consumate</p>
                  <p className="font-bold">{summary.totalMaterialsConsumed}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Ore Suplimentare</p>
                  <p className="font-bold text-green-600">{summary.totalOvertimeHours}h</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {teamSummaries.length === 0 && !isLoadingData && (
        <Card>
          <CardContent className="text-center py-12">
            <Activity className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-lg font-medium text-gray-600">Nu există date pentru perioada selectată</p>
            <p className="text-sm text-gray-500 mt-2">Selectați o altă perioadă sau ajustați filtrele</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};